import type { SwymItemPayload, SwymListItem } from './swym.types'

/**
 * Helpers for reading a Swym list entry.
 *
 * The "shopper picked this size" flag lives in `cprops`, not in the `du`: Swym
 * normalises `du` to origin plus path and drops any query string. `cprops` is
 * Swym's documented field for frontend attributes, so `note` stays free for
 * shopper-facing annotations.
 */

/** The exact `cprops` payload a size-explicit save sends. */
export const SIZE_SELECTED_CPROPS = { sizeSelected: true } as const

/**
 * Debounce for persisting a card-driven change (a size chip, a swatch swap)
 * to Swym. Shared by `WishlistSizeSelector` and `ResultCard` so both settle
 * on the same interval: a chip-through or a quick swatch flip fires one
 * replace, not one per tap/click.
 */
export const PERSIST_DEBOUNCE_MS = 400

/** Product handle from a stored `du`, ignoring any query string or fragment. */
export function handleFromDu(du?: string | null): string | null {
  const match = /\/products\/([^/?#]+)/.exec(du ?? '')
  return match ? match[1] : null
}

/**
 * Whether the shopper explicitly picked this entry's size, as opposed to a
 * product-level save that defaulted to a variant (PLP hearts, and anything
 * saved before this feature shipped). The single place `cprops` is read —
 * every other consumer (including `swym-service.ts`) goes through this or
 * `savedVariantId` rather than reading `item.cprops` itself.
 */
export function isSizeSelected(item?: SwymListItem | SwymItemPayload | null): boolean {
  return Boolean(item?.cprops?.sizeSelected)
}

/**
 * The size the shopper explicitly chose, or null for a product-level entry.
 * Every entry stores a real `epi`, so the flag — not the presence of `epi` — is
 * what makes a size deliberate.
 */
export function savedVariantId(item?: SwymListItem | null): number | null {
  // `item?.epi`, not `item.epi`: `isSizeSelected` being true implies `item` is
  // set, but that's not something TS can narrow through a function call.
  return isSizeSelected(item) ? Number(item?.epi) : null
}
